// The request: JSON or a form, the picker when it's empty, the lesson's ghost
// chip, and Send. See Design > Exchange, "Send a request".

import { useState } from "preact/hooks";
import { Button, ChoiceRow, Field, Icon, MoneyInput, PillGroup, Pointer, Select, StatusBadge, TextInput } from "../../../../ui/src/index.ts";
import type { PillOption } from "../../../../ui/src/index.ts";
import { LIMITS, firstQuestion, withFirstQuestion, withMaxTotalCost } from "../../domain/request.ts";
import type { AskRequest } from "../../domain/types.ts";
import { lessonView, pick, readRequest, send, sendBlocked, setRequest, setRequestText, applyChip } from "../actions.ts";
import { setState, useStore } from "../store.ts";

const wordCount = (t: string) => t.trim().split(/\s+/).filter(Boolean).length;
const REQUEST_VIEWS: PillOption<"form" | "json">[] = [{ value: "form", label: "Form" }, { value: "json", label: "JSON" }];

/** The empty requests to start from: one question, several, or one for certain hubs. The Playground's start screen and the picker below share them. */
export const BLANK_STARTS = {
  question: (text = "") => pick("question", text),
  questions: () => setRequest({ questions: [{ id: "q1", text: "" }, { id: "q2", text: "" }] }),
  hubs: () => setRequest({ questions: [{ id: "q1", text: "", hubs: ["**"] }] }),
};

function Picker({ hot, text }: { hot: boolean; text: string }) {
  const option = (title: string, desc: string, hint: string, on: () => void, callout = false) => (
    <Pointer active={callout} class="cs-pick-wrap">
      <ChoiceRow title={title} sub={desc} hint={`Example: ${hint}`} arrow={false} onSelect={on} />
    </Pointer>
  );
  return (
    <div class="cs-picker">
      <div class="cs-picker__head">Start a request <a href="https://docs.warmhub.ai" target="_blank" rel="noreferrer">Docs</a></div>
      <div class="ui-list">
        {option("Question", "Ask every hub one question", `"A question in plain words"`, () => BLANK_STARTS.question(text), hot)}
        {option("Questions", `Send up to ${LIMITS.questions} questions in one request`, "two questions in one request", BLANK_STARTS.questions)}
        {option("Hub targets", "Limit which hubs get a question", `"<org>/**"`, BLANK_STARTS.hubs)}
      </div>
    </div>
  );
}

function Form({ request }: { request: AskRequest | null }) {
  const q = firstQuestion(request);
  const text = q.text ?? "";
  const change = (c: Parameters<typeof withFirstQuestion>[1], cleared?: Parameters<typeof withFirstQuestion>[2]) => setRequest(withFirstQuestion(request, c, cleared));
  const number = (v: string): number | undefined => (v === "" ? undefined : Number(v));
  return (
    <div class="cs-form">
      <Field id="f-text" label="Question" hint={`${wordCount(text)} of ${LIMITS.words} words · ${text.length} of ${LIMITS.characters} characters`}>
        {(attrs) => <textarea {...attrs} class="ui-textarea" rows={3} value={text} onInput={(e) => change({ text: e.currentTarget.value })} />}
      </Field>
      <Field id="f-bid" label="Max price per answer, in US dollars per million answers" hint="You pay the hub's ask, never more than this.">
        {(attrs) => <MoneyInput {...attrs} type="number" min="0" step="any" value={q.maxCostPerAnswer ?? ""} onInput={(e) => { const v = number(e.currentTarget.value); if (v === undefined) change({}, "maxCostPerAnswer"); else change({ maxCostPerAnswer: v }); }} />}
      </Field>
      <div class="cs-form__grid">
        <Field id="f-max" label="Max answers">
          {(attrs) => <Select {...attrs} value={String(q.maxAnswers ?? 1)} onChange={(e) => change({ maxAnswers: Number(e.currentTarget.value) })}>{[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}</Select>}
        </Field>
        <Field id="f-hubs" label="Hub targets">
          {(attrs) => <TextInput {...attrs} placeholder="Every hub" value={(q.hubs ?? []).join(", ")} onInput={(e) => { const v = e.currentTarget.value.split(",").map((h) => h.trim()).filter(Boolean); if (v.length) change({ hubs: v }); else change({}, "hubs"); }} />}
        </Field>
      </div>
      <Field id="f-cap" label="Total cost limit">
        {(attrs) => <MoneyInput {...attrs} type="number" min="0" step="any" placeholder="No limit" value={request?.maxTotalCost ?? ""} onInput={(e) => setRequest(withMaxTotalCost(request, number(e.currentTarget.value)))} />}
      </Field>
      {(request?.questions?.length ?? 0) > 1 && <p class="cs-hint">{(request?.questions?.length ?? 1) - 1} more questions are in the JSON view.</p>}
    </div>
  );
}

export function RequestPanel() {
  const s = useStore((x) => x);
  const { request, problems, empty } = readRequest(s);
  // Problems wait until the person has typed in the request: a request just started from a choice is
  // unfinished, not wrong. The Playground remounts this panel for each new request and lesson.
  const [typed, setTyped] = useState(false);
  const flagged = typed && problems.length > 0;
  const badge = problems.length === 0 ? "Ready to send" : !typed ? "Fill in to send" : problems.length === 1 ? "1 problem" : `${problems.length} problems`;
  const lv = lessonView(s);
  const blocked = sendBlocked(s);
  const step = lv?.step ?? null;
  const callSend = step?.kind === "send" && !blocked && !s.sending;
  const pickHot = step?.kind === "pick";
  const chip = lv?.chip ?? null;
  const addQuestion = () => {
    const base = request ?? {};
    const qs = base.questions ?? [];
    if (qs.length < LIMITS.questions) setRequest({ ...base, questions: [...qs, { id: `q${qs.length + 1}`, text: "" }] });
  };

  return (
    <section class="cs-pane cs-request" aria-label="Request" onInput={() => setTyped(true)}>
      <div class="cs-pane__head">
        <h2>Request</h2>
        <div class="cs-pane__tools">
          <span title={problems.join(" ")}><StatusBadge tone={flagged ? "danger" : "neutral"}>{badge}</StatusBadge></span>
          <PillGroup label="Request view" options={REQUEST_VIEWS} value={s.requestView} onChange={(v) => setState({ requestView: v })} />
        </div>
      </div>
      <div class="cs-pane__body cs-request__body">
        {empty ? <Picker hot={pickHot} text={step?.kind === "pick" ? step.text : ""} /> : s.requestView === "form" ? <Form request={request} /> : (
          <>
            <label class="ui-sr-only" for="json">Request JSON</label>
            <textarea id="json" class="ui-textarea cs-json" spellcheck={false} value={s.requestText} onInput={(e) => setRequestText(e.currentTarget.value)} />
            {flagged && <ul class="cs-problems" aria-live="polite">{problems.slice(0, 4).map((p) => <li key={p}>{p}</li>)}</ul>}
          </>
        )}
        {chip && !empty && (
          <Pointer active class="cs-chip-wrap">
            <Button size="sm" onClick={applyChip}><Icon name="plus" size={16} />{chip.label}</Button>
          </Pointer>
        )}
      </div>
      <div class="cs-pane__foot">
        <div class="cs-request__left">
          <Button variant="ghost" icon aria-label="Add a question" onClick={addQuestion} disabled={empty}><Icon name="plus" /></Button>
          <span class="mono cs-endpoint">POST /v1/ask</span>
        </div>
        <Pointer active={callSend}>
          <Button variant="solid" disabled={blocked !== null} title={blocked ?? "Send the request"} onClick={() => void send()}>
            {s.sending ? "Sending…" : "Send request"} <span class="mono cs-kbd" aria-hidden="true">⌘↵</span>
          </Button>
        </Pointer>
      </div>
    </section>
  );
}
